import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle, ChatsCircle, MagnifyingGlass, UsersThree, X } from "@phosphor-icons/react";
import { messagesApi, usersApi } from "@/lib/apiClient";
import { toast } from "sonner";

export default function NewConversationModal({ open, onClose, onConversationReady, onRequestSent, initialHandle }) {
    const [mode, setMode] = useState("direct");
    const [query, setQuery] = useState(initialHandle || "");
    const [results, setResults] = useState([]);
    const [selected, setSelected] = useState([]);
    const [text, setText] = useState("");
    const [groupTitle, setGroupTitle] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setQuery(initialHandle || "");
        setSelected([]);
        setText("");
        setGroupTitle("");
        setMode("direct");
    }, [open, initialHandle]);

    useEffect(() => {
        if (!open || query.trim().length < 1) {
            setResults([]);
            return undefined;
        }
        const timer = setTimeout(() => {
            usersApi.search({ q: query.trim(), limit: 8 }).then((data) => setResults(data.items || [])).catch(() => setResults([]));
        }, 180);
        return () => clearTimeout(timer);
    }, [open, query]);

    const selectedDirect = selected[0] || null;
    const canSubmit = useMemo(() => {
        if (mode === "group") return groupTitle.trim() && selected.length >= 2;
        return selectedDirect && text.trim();
    }, [mode, groupTitle, selected, selectedDirect, text]);

    // Helper: readable group size validation hint
    const groupMemberHint = mode === "group" && selected.length === 1
        ? "Add at least 1 more traveler to create a group."
        : null;

    const toggleUser = (user) => {
        if (mode === "direct") {
            setSelected([user]);
            setQuery(`@${user.handle}`);
            return;
        }
        setSelected((prev) => prev.some((item) => item.id === user.id) ? prev.filter((item) => item.id !== user.id) : [...prev, user]);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setBusy(true);
        try {
            if (mode === "group") {
                const group = await messagesApi.createGroupConversation({
                    title: groupTitle.trim(),
                    member_handles: selected.map((item) => item.handle),
                });
                if (text.trim()) await messagesApi.sendMessage(group.id, { text: text.trim() });
                toast.success("Group conversation ready.");
                onConversationReady?.(group);
                return;
            }

            const handle = selectedDirect.handle;
            try {
                const conversation = await messagesApi.createDirectConversation(handle);
                const sent = await messagesApi.sendMessage(conversation.id, { text: text.trim() });
                toast.success("Message sent.");
                onConversationReady?.({ ...conversation, seededMessage: sent });
            } catch (error) {
                if (error?.response?.status === 403) {
                    const request = await messagesApi.sendRequest({ handle, text: text.trim() });
                    toast.success("Message request sent.");
                    onRequestSent?.(request);
                    return;
                }
                throw error;
            }
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Could not start this conversation.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[95] flex items-center justify-center p-4" style={{ background: "rgba(5,10,17,0.88)", backdropFilter: "blur(10px)" }} onClick={onClose} data-testid="new-conversation-modal">
                    <motion.form initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 14, opacity: 0 }} onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl max-h-[88vh] rounded-[32px] p-6 md:p-7 flex flex-col overflow-hidden shadow-2xl" style={{ background: "var(--navy)", border: "1px solid rgba(212,175,55,0.25)" }}>
                        <div className="flex items-start justify-between gap-4 mb-4 shrink-0">
                            <div>
                                <div className="overline mb-1.5">Passport messages</div>
                                <h3 className="font-serif text-2xl md:text-3xl text-white">Start a new conversation</h3>
                                <p className="text-xs md:text-sm text-white/55 mt-1">Passport will create a normal chat when privacy allows it, or a message request when it doesn&apos;t.</p>
                            </div>
                            <button type="button" onClick={onClose} className="w-9 h-9 rounded-full glass grid place-items-center shrink-0" data-testid="new-conversation-close"><X size={15} /></button>
                        </div>

                        <div className="flex gap-2 p-1 rounded-full mb-4 w-max shrink-0" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}>
                            {[{ key: "direct", label: "Direct", icon: ChatsCircle }, { key: "group", label: "Group", icon: UsersThree }].map((item) => {
                                const Icon = item.icon;
                                return (
                                    <button key={item.key} type="button" onClick={() => setMode(item.key)} className="px-3.5 py-1.5 rounded-full text-xs font-medium inline-flex items-center gap-1.5" style={{ background: mode === item.key ? "var(--gold)" : "transparent", color: mode === item.key ? "var(--navy)" : "white" }} data-testid={`new-conversation-mode-${item.key}`}>
                                        <Icon size={13} /> {item.label}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="flex-1 min-h-0 overflow-y-auto space-y-3.5 px-1 py-1">
                            {mode === "group" && (
                                <input value={groupTitle} onChange={(e) => setGroupTitle(e.target.value)} placeholder="Group title" className="w-full rounded-2xl px-4 py-2.5 bg-black/25 text-white border border-white/10 text-sm outline-none transition-all focus:relative focus:z-10 focus:border-white/60 focus:ring-1 focus:ring-white/30" data-testid="new-group-title-input" />
                            )}

                            <div className="relative focus-within:z-10">
                                <MagnifyingGlass size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={mode === "group" ? "Search travelers to add" : "Search a traveler by name or handle"} className="w-full rounded-2xl pl-11 pr-4 py-2.5 bg-black/25 text-white border border-white/10 text-sm outline-none transition-all focus:relative focus:z-10 focus:border-white/60 focus:ring-1 focus:ring-white/30" data-testid="new-conversation-search-input" />
                            </div>

                            {selected.length > 0 && (
                                <div className="flex flex-wrap gap-2" data-testid="new-conversation-selected-users">
                                    {selected.map((item) => (
                                        <button key={item.id} type="button" onClick={() => toggleUser(item)} className="px-3 py-1 rounded-full text-xs border inline-flex items-center gap-1.5" style={{ background: "rgba(212,175,55,0.12)", borderColor: "rgba(212,175,55,0.3)", color: "var(--gold)" }} data-testid={`new-conversation-selected-${item.handle}`}>
                                            <CheckCircle size={12} weight="fill" /> @{item.handle}
                                        </button>
                                    ))}
                                </div>
                            )}
                            {groupMemberHint && (
                                <div className="text-xs text-white/45" data-testid="new-group-member-hint">{groupMemberHint}</div>
                            )}

                            <div className="max-h-48 overflow-y-auto rounded-2xl border" style={{ borderColor: "rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.03)" }} data-testid="new-conversation-search-results">
                                {results.length === 0 ? (
                                    <div className="px-4 py-6 text-center text-xs text-white/45">Search Passport travelers to start a chat or send a message request.</div>
                                ) : results.map((item) => {
                                    const picked = selected.some((entry) => entry.id === item.id);
                                    return (
                                        <button key={item.id} type="button" onClick={() => toggleUser(item)} className="w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-white/5 border-b last:border-b-0" style={{ borderColor: "rgba(255,255,255,0.04)" }} data-testid={`new-conversation-result-${item.handle}`}>
                                            <img src={item.avatar_url || `https://api.dicebear.com/7.x/notionists/svg?seed=${item.handle}`} alt="" className="w-10 h-10 rounded-full object-cover" />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm text-white truncate">{item.name}</div>
                                                <div className="text-xs text-white/50 truncate">@{item.handle}</div>
                                            </div>
                                            <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: picked ? "var(--gold)" : "rgba(255,255,255,0.35)" }}>{picked ? "Selected" : item.tier}</div>
                                        </button>
                                    );
                                })}
                            </div>

                            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder={mode === "group" ? "Optional first message to your group" : "Write the first message for this chat or request"} className="w-full rounded-2xl px-4 py-2.5 bg-black/25 text-white border border-white/10 text-sm resize-none outline-none transition-all focus:relative focus:z-10 focus:border-white/60 focus:ring-1 focus:ring-white/30" data-testid="new-conversation-text-input" />
                        </div>

                        <div className="flex justify-end gap-3 pt-4 mt-2 border-t shrink-0" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                            <button type="button" onClick={onClose} className="btn-ghost text-xs md:text-sm !py-2 !px-4" data-testid="new-conversation-cancel">Cancel</button>
                            <button type="submit" disabled={busy || !canSubmit} className="btn-gold text-xs md:text-sm !py-2 !px-5 disabled:opacity-50" data-testid="new-conversation-submit">
                                {busy ? "Working…" : mode === "group" ? "Create group" : "Send"}
                            </button>
                        </div>
                    </motion.form>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
